import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text
} from '@fluentui/react-components';
import { LockClosed16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { ContractReview } from '../../../../models/Entities/Contracts/ContractReview';
import { describeChange, getApplicableFindings, splitNewAddendumReview, summarizeReview } from '../ContractReview.Functions';
import { useContractReviewStyles } from '../ContractReview.Styles';
import { useCodeText, useFieldLabel, useValueLabel } from '../ContractReview.Text';

// Confirmación de "Aplicar aceptados" (US5) y de "Crear adenda y aplicar" (US9).

export interface ContractReviewApplyDialogProps {
  open: boolean;
  review: ContractReview;
  baseAddendumName?: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const formatValidity = (t: TFunction, from?: string | null, to?: string | null): string => {
  const date = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '—');
  return to ? t('contractReview.newAddendum.validityRange', { from: date(from), to: date(to) }) : t('contractReview.newAddendum.validityOpen', { from: date(from) });
};

export const ContractReviewApplyDialog: React.FC<ContractReviewApplyDialogProps> = ({ open, review, baseAddendumName, busy, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const styles = useContractReviewStyles();
  const codeText = useCodeText();
  const isNewAddendum = review.mode === 'NewAddendum';

  const applicable = getApplicableFindings(review);
  const summary = summarizeReview(review);
  const split = splitNewAddendumReview(review);
  const changes = isNewAddendum ? applicable.filter(f => f !== split.finding && f !== split.baseValidityFinding) : applicable;
  const baseAdjustment = !!split.baseValidityFinding && applicable.includes(split.baseValidityFinding);
  const canConfirm = isNewAddendum ? split.canCreate : applicable.length > 0;
  // Cada cambio en una línea legible, "Consulta — Modificar concepto: Precio: 150 → 180", en texto plano.
  const fieldLabel = useFieldLabel();
  const valueLabel = useValueLabel();
  const changeText = (f: (typeof applicable)[number]) => {
    const lines = describeChange(f).map(
      line =>
        `${fieldLabel(line.field)}: ${line.current !== undefined ? `${valueLabel(line.rawCurrent)} → ` : ''}${
          line.ref ? t('contractReview.item.refPending', { label: line.ref.label }) : valueLabel(line.rawNext)
        }`
    );
    const subject = f.target?.label ?? f.title;
    return `${subject} — ${codeText('patchOp', f.patch!.op)} ${codeText('entityType', f.patch!.entityType).toLowerCase()}${lines.length ? ': ' + lines.join(' · ') : ''}`;
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onCancel()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNewAddendum ? t('contractReview.apply.createAddendum') : t('contractReview.apply.title')}</DialogTitle>
          <DialogContent>
            <div className={styles.section}>
              {isNewAddendum && split.fields && (
                <Text>
                  {t('contractReview.apply.newAddendumConfirm', {
                    name: split.fields.ContractAddendumName,
                    validity: formatValidity(t, split.fields.ContractAddendumExpiryStartDate, split.fields.ContractAddendumExpiryEndDate),
                    base: baseAddendumName ?? '—',
                    count: changes.length
                  })}
                </Text>
              )}
              {isNewAddendum && baseAdjustment && <Text>{t('contractReview.apply.baseAdjustment')}</Text>}
              {!isNewAddendum && (applicable.length > 0 ? <Text>{t('contractReview.apply.count', { count: applicable.length })}</Text> : <Text>{t('contractReview.apply.noApplicable')}</Text>)}

              {changes.length > 0 && (
                <ul aria-label={t('contractReview.apply.title')}>
                  {changes.map(f => (
                    <li key={f.id}>{changeText(f)}</li>
                  ))}
                </ul>
              )}

              {summary.acceptedWithoutPatch > 0 && <Text className={styles.muted}>{t('contractReview.apply.acceptedWithoutPatch', { count: summary.acceptedWithoutPatch })}</Text>}
              {!isNewAddendum && review.baseAddendumIsBlocked === true && (
                <MessageBar intent="warning" icon={<LockClosed16Regular />}>
                  <MessageBarBody>{t('contractReview.apply.blocked')}</MessageBarBody>
                </MessageBar>
              )}
              {isNewAddendum && !split.canCreate && (
                <MessageBar intent="warning">
                  <MessageBarBody>{split.finding ? t('contractReview.apply.newAddendumNotAccepted') : t('contractReview.newAddendum.noCreate')}</MessageBarBody>
                </MessageBar>
              )}
              <Text className={styles.muted}>{t('contractReview.apply.audit')}</Text>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              {t('contractReview.apply.cancel')}
            </Button>
            <Button appearance="primary" disabled={!canConfirm || busy} icon={busy ? <Spinner size="tiny" /> : undefined} onClick={onConfirm}>
              {busy ? t('contractReview.apply.applying') : isNewAddendum ? t('contractReview.apply.createAddendum') : t('contractReview.apply.confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ContractReviewApplyDialog;
